import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useCartStore from '../../store/cartStore';
import { useWishlist } from '../../hooks/useWishlist';
import { useAuthModal } from '../../context/AuthModalContext';
import { getSearchSuggestionsApi } from '../../api/product.api';
import { ROUTES, APP_NAME } from '../../constants';
import Avatar from '../ui/Avatar';
import NotificationBell from '../common/NotificationBell';
import cn from '../../utils/cn';
import useDebounce from '../../hooks/useDebounce';

const CartIcon = ({ count = 0 }) => (
  <Link to="/cart" className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors no-underline">
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-10H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
    {count > 0 && (
      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
        {count > 99 ? '99+' : count}
      </span>
    )}
  </Link>
);

const WishlistIcon = ({ count = 0 }) => (
  <Link to="/wishlist" className="relative p-2 text-gray-600 hover:text-red-500 transition-colors no-underline">
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
    {count > 0 && (
      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
        {count}
      </span>
    )}
  </Link>
);

const SearchBar = ({ onSearch }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debouncedQ = useDebounce(query, 300);
  const ref = useRef(null);

  useEffect(() => {
    if (debouncedQ.trim().length < 2) { setSuggestions([]); return; }
    getSearchSuggestionsApi(debouncedQ).then((r) => {
      setSuggestions(r.data.data || []);
      setOpen(true);
    }).catch(() => {});
  }, [debouncedQ]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) { navigate(`/products?search=${encodeURIComponent(query.trim())}`); setQuery(''); setOpen(false); }
  };

  const pick = (slug) => { navigate(`/products/${slug}`); setQuery(''); setOpen(false); };

  return (
    <div ref={ref} className="relative flex-1 max-w-xl hidden md:block">
      <form onSubmit={handleSubmit}>
        <div className="flex w-full rounded-xl border border-gray-200 overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            placeholder="Search products..."
            className="flex-1 px-4 py-2 text-sm outline-none bg-white text-gray-800 placeholder-gray-400"
          />
          <button type="submit" className="px-4 bg-primary-600 hover:bg-primary-700 text-white transition-colors flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="text-sm font-medium hidden lg:block">Search</span>
          </button>
        </div>
      </form>
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-100 shadow-lg z-50 overflow-hidden">
          {suggestions.map((p) => (
            <button key={p._id} onClick={() => pick(p.slug)}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left">
              <img src={p.images?.[0]?.url || 'https://placehold.co/40x40?text=P'} alt={p.name}
                className="w-9 h-9 rounded-lg object-cover border border-gray-100 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                <p className="text-xs text-primary-600 font-semibold">Rs. {Number(p.price).toLocaleString()}</p>
              </div>
            </button>
          ))}
          <button onClick={handleSubmit}
            className="w-full px-4 py-2.5 text-xs text-primary-600 font-medium hover:bg-primary-50 border-t border-gray-100 text-center transition-colors">
            See all results for "{query}" →
          </button>
        </div>
      )}
    </div>
  );
};

const HelpDropdown = ({ onClose }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { isAuthenticated } = useAuthStore();
  const { openLoginModal } = useAuthModal();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const items = [
    { icon: '🎧', label: 'Help Center',           to: '/help' },
    { icon: '❓', label: 'FAQ',                   to: '/faq' },
    { icon: '📦', label: 'Order',                 to: '/orders' },
    { icon: '🚚', label: 'Shipping & Delivery',   to: '/help#shipping' },
    { icon: '💳', label: 'Payment',               to: '/help#payment' },
    { icon: '↩️', label: 'Returns & Refunds',     to: '/help#returns' },
  ];

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors w-full md:w-auto">
        Help & Support
        <svg className={`w-3.5 h-3.5 transition-transform ml-auto md:ml-0 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-20 animate-fade-in">
            {items.map(item => (
              <Link key={item.label} to={item.to} onClick={() => { setOpen(false); onClose?.(); }}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 no-underline transition-colors">
                <span className="text-base">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Navbar = ({ isPublic }) => {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { cart, fetchCart } = useCartStore();
  const { data: wishlist } = useWishlist();
  const { openLoginModal } = useAuthModal();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState('');

  const cartCount     = cart?.items?.reduce((s, i) => s + i.quantity, 0) ?? 0;
  const wishlistCount = wishlist?.length ?? 0;

  useEffect(() => { if (isAuthenticated) fetchCart(); }, [isAuthenticated]);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [navigate]);

  const handleLogout = () => { logout(); navigate(ROUTES.HOME); setMobileMenuOpen(false); };

  const handleMobileSearch = (e) => {
    e.preventDefault();
    if (mobileSearch.trim()) {
      navigate(`/products?search=${encodeURIComponent(mobileSearch.trim())}`);
      setMobileSearch('');
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      {isPublic && (
        <div className="bg-primary-600 text-white text-xs text-center py-1.5 px-4">
          Free shipping on orders over Rs. 2,000 — Shop Now
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">

          {/* Logo */}
          <Link to={isPublic ? '/' : ROUTES.DASHBOARD} className="flex items-center no-underline shrink-0">
            <img src="/assets/f-logo.png" alt={APP_NAME} className="h-9 w-auto" />
          </Link>

          {/* Desktop search */}
          {isPublic && <SearchBar />}

          {/* Desktop nav links */}
          {isPublic && (
            <div className="hidden md:flex items-center gap-1 shrink-0">
              {[{ to: '/', label: 'Home', end: true }, { to: '/products', label: 'Products' }].map(({ to, label, end }) => (
                <NavLink key={to} to={to} end={end}
                  className={({ isActive }) => cn('px-3 py-2 rounded-lg text-sm font-medium transition-colors no-underline',
                    isActive ? 'text-primary-600' : 'text-gray-600 hover:text-gray-900')}>
                  {label}
                </NavLink>
              ))}
              {isAuthenticated && (
                <NavLink to="/orders"
                  className={({ isActive }) => cn('px-3 py-2 rounded-lg text-sm font-medium transition-colors no-underline',
                    isActive ? 'text-primary-600' : 'text-gray-600 hover:text-gray-900')}>
                  My Orders
                </NavLink>
              )}
              <HelpDropdown />
            </div>
          )}

          {/* Right icons */}
          <div className="flex items-center gap-1 shrink-0">
            {isPublic && isAuthenticated && <WishlistIcon count={wishlistCount} />}
            {isPublic && <CartIcon count={cartCount} />}
            {isAuthenticated && <NotificationBell />}

            {isAuthenticated ? (
              <div className="relative">
                <button onClick={() => setDropdownOpen(o => !o)}
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-gray-100 transition-colors">
                  <Avatar src={user?.avatar} name={user?.name} size="sm" />
                  <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[100px] truncate">{user?.name}</span>
                  <svg className="w-4 h-4 text-gray-400 hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {dropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20 animate-fade-in">
                      <Link to={ROUTES.PROFILE} onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 no-underline">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        Profile
                      </Link>
                      {isPublic && (
                        <Link to="/wishlist" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 no-underline">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                          Wishlist {wishlistCount > 0 && `(${wishlistCount})`}
                        </Link>
                      )}
                      {isPublic && (
                        <Link to={ROUTES.DASHBOARD} onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 no-underline">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                          Dashboard
                        </Link>
                      )}
                      {['seller', 'admin', 'super_admin'].includes(user?.role) && (
                        <Link to="/seller" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 no-underline">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                          Seller Hub
                        </Link>
                      )}
                      {user?.role === 'user' && (
                        <Link to={ROUTES.BECOME_SELLER} onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 no-underline font-medium">
                          <span>🏪</span> Become a Seller
                        </Link>
                      )}
                      {['admin', 'super_admin'].includes(user?.role) && (
                        <Link to="/admin" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 no-underline">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                          {user?.role === 'super_admin' ? 'Super Admin Panel' : 'Admin Panel'}
                        </Link>
                      )}
                      <hr className="my-1 border-gray-100" />
                      <button onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => openLoginModal('login')}
                  className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900">Login</button>
                <button onClick={() => openLoginModal('register')}
                  className="px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">Sign up</button>
              </div>
            )}

            {/* Hamburger — mobile only */}
            {isPublic && (
              <button onClick={() => setMobileMenuOpen(o => !o)}
                className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors ml-1"
                aria-label="Toggle menu">
                {mobileMenuOpen ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search bar */}
      {isPublic && (
        <div className="md:hidden px-4 pb-3">
          <form onSubmit={handleMobileSearch} className="flex rounded-xl border border-gray-200 overflow-hidden">
            <input type="text" value={mobileSearch} onChange={(e) => setMobileSearch(e.target.value)}
              placeholder="Search products..."
              className="flex-1 px-4 py-2 text-sm outline-none" />
            <button type="submit" className="px-4 bg-primary-600 text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </form>
        </div>
      )}

      {/* Mobile menu drawer */}
      {isPublic && mobileMenuOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setMobileMenuOpen(false)} />
          <div className="md:hidden absolute top-full left-0 right-0 z-50 bg-white border-t border-gray-100 shadow-lg animate-fade-in">
            <div className="px-4 py-3 space-y-1">
              {[
                { to: '/', label: '🏠 Home', end: true },
                { to: '/products', label: '🛍️ Products' },
                ...(isAuthenticated ? [{ to: '/orders', label: '📦 My Orders' }] : []),
                ...(isAuthenticated && ['seller', 'admin', 'super_admin'].includes(user?.role)
                  ? [{ to: '/seller', label: '🏪 Seller Hub' }] : []),
                ...(isAuthenticated && ['admin', 'super_admin'].includes(user?.role)
                  ? [{ to: '/admin', label: '⚙️ Admin Panel' }] : []),
              ].map(({ to, label, end }) => (
                <NavLink key={to} to={to} end={end} onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => cn('block px-3 py-2.5 rounded-xl text-sm font-medium no-underline transition-colors',
                    isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50')}>
                  {label}
                </NavLink>
              ))}
              <div className="pt-1 border-t border-gray-100">
                <HelpDropdown onClose={() => setMobileMenuOpen(false)} />
              </div>
              {!isAuthenticated && (
                <div className="flex gap-2 pt-2">
                  <button onClick={() => { openLoginModal('login'); setMobileMenuOpen(false); }}
                    className="flex-1 py-2.5 text-sm font-medium border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50">
                    Login
                  </button>
                  <button onClick={() => { openLoginModal('register'); setMobileMenuOpen(false); }}
                    className="flex-1 py-2.5 text-sm font-medium bg-primary-600 text-white rounded-xl hover:bg-primary-700">
                    Sign up
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
